import { Box, Group, Select, SelectItem, SelectProps, Stack } from '@mantine/core';
import { IconBolt, IconPlus } from '@tabler/icons-react';
import { useCreateRevisionPredefinedDataModal } from 'components/modals/create-revision-predefined-data-modal/CreateRevisionPredefinedDataModalProvider';
import P3Regular from 'components/typography/P3Regular';
import panic from 'errors/panic';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import {
  createOneTimeRevisionPredefinedDataOption,
  hasRevisionPredefinedDataExactMatch,
} from 'components/inputs/revision-predefined-data/helpers';
import {
  RevisionPredefinedDataOption,
  RevisionPredefinedDataSelection,
} from 'components/inputs/revision-predefined-data/types';

type RevisionPredefinedDataActionType = 'one-time' | 'save';

interface RevisionPredefinedDataActionItem extends SelectItem {
  actionType: RevisionPredefinedDataActionType;
  kind: 'action';
}

interface RevisionPredefinedDataPickerProps {
  value?: RevisionPredefinedDataSelection;
  options: RevisionPredefinedDataOption[];
  reservedNames?: string[];
  loading?: boolean;
  hideDescription?: boolean;
  loadingPlaceholder?: string;
  deviceTypeName: string;
  predefinedDataTypeId: number;
  predefinedDataTypeName: string;
  onChange?: (value?: RevisionPredefinedDataSelection) => void;
  onCatalogOptionCreate?: (
    option: Pick<RevisionPredefinedDataOption, 'value' | 'label' | 'description' | 'origin'>
  ) => void;
}

type Props = RevisionPredefinedDataPickerProps & Omit<SelectProps, 'data' | 'onChange' | 'value'>;

const ACTION_VALUE_PREFIX = '__revision-predefined-data-action__';

/**
 * Builds a stable synthetic select value for action items.
 */
function toActionValue(actionType: RevisionPredefinedDataActionType, query: string) {
  return `${ACTION_VALUE_PREFIX}:${actionType}:${encodeURIComponent(query)}`;
}

/**
 * Parses the select value if it represents an action item.
 */
function fromActionValue(value: string): { actionType: RevisionPredefinedDataActionType; query: string } | null {
  if (!value.startsWith(ACTION_VALUE_PREFIX)) {
    return null;
  }

  const [, actionType, encodedQuery] = value.split(':');

  if ((actionType !== 'one-time' && actionType !== 'save') || !encodedQuery) {
    return null;
  }

  return {
    actionType,
    query: decodeURIComponent(encodedQuery),
  };
}

interface PickerItemProps extends React.ComponentPropsWithoutRef<'div'> {
  label: string;
  kind?: 'action';
  actionType?: RevisionPredefinedDataActionType;
}

const PickerItem = forwardRef<HTMLDivElement, PickerItemProps>(function PickerItem(
  { label, kind, actionType, ...others }: PickerItemProps,
  ref
) {
  if (kind !== 'action') {
    return (
      <div ref={ref} {...others}>
        {label}
      </div>
    );
  }

  return (
    <div ref={ref} {...others}>
      <Group noWrap spacing={8}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {actionType === 'one-time' ? <IconBolt size={16} stroke={1.5} /> : <IconPlus size={16} stroke={1.5} />}
        </Box>
        <span>{label}</span>
      </Group>
    </div>
  );
});

/**
 * Creatable revision-only predefined data picker with one-time and save actions.
 */
export default function RevisionPredefinedDataPicker({
  value,
  options,
  reservedNames = [],
  loading = false,
  hideDescription = false,
  loadingPlaceholder = 'Načítavanie ...',
  deviceTypeName,
  predefinedDataTypeId,
  predefinedDataTypeName,
  onChange,
  onCatalogOptionCreate,
  ...props
}: Props) {
  const { openCreateRevisionPredefinedDataModal } = useCreateRevisionPredefinedDataModal();
  const [searchValue, setSearchValue] = useState(value?.name ?? '');

  useEffect(() => {
    setSearchValue(value?.name ?? '');
  }, [value?.description, value?.name, value?.source]);

  const resolvedOptions = useMemo(() => {
    if (!value?.name || value.source !== 'one-time' || options.some((option) => option.value === value.name)) {
      return options;
    }

    return [createOneTimeRevisionPredefinedDataOption(value), ...options];
  }, [options, value]);

  const actionItems = useMemo(() => {
    const query = searchValue.trim();
    const queryExistsInReservedNames = reservedNames.some(
      (name) => name.trim().toLocaleLowerCase() === query.toLocaleLowerCase()
    );

    if (!query || queryExistsInReservedNames || hasRevisionPredefinedDataExactMatch(resolvedOptions, query)) {
      return [] as RevisionPredefinedDataActionItem[];
    }

    return [
      {
        value: toActionValue('one-time', query),
        label: `Použiť jednorazovo: "${query}"`,
        group: 'Možnosti',
        actionType: 'one-time',
        kind: 'action',
      },
      {
        value: toActionValue('save', query),
        label: `Uložiť ako vlastný predvolený údaj: "${query}"`,
        group: 'Možnosti',
        actionType: 'save',
        kind: 'action',
      },
    ];
  }, [reservedNames, resolvedOptions, searchValue]);

  const data = useMemo(() => [...actionItems, ...resolvedOptions], [actionItems, resolvedOptions]);
  const optionsByValue = useMemo(
    () => new Map(resolvedOptions.map((option) => [option.value, option])),
    [resolvedOptions]
  );

  /**
   * Propagates the resolved selection and keeps the search input aligned with it.
   */
  const handleResolvedSelection = (selection?: RevisionPredefinedDataSelection) => {
    onChange?.(selection);
    setSearchValue(selection?.name ?? '');
  };

  /**
   * Handles normal option selections as well as the synthetic one-time/save actions.
   */
  const handleSelectChange = async (selectedValue: string | null) => {
    if (!selectedValue) {
      handleResolvedSelection(undefined);
      return;
    }

    const action = fromActionValue(selectedValue);

    if (action?.actionType === 'one-time') {
      handleResolvedSelection({
        name: action.query,
        description: '',
        source: 'one-time',
      });
      return;
    }

    if (action?.actionType === 'save') {
      const createdValue = await openCreateRevisionPredefinedDataModal({
        initialName: action.query,
        deviceTypeName,
        predefinedDataTypeId,
        predefinedDataTypeName,
      });

      if (!createdValue) {
        setSearchValue(value?.name ?? '');
        return;
      }

      onCatalogOptionCreate?.({
        value: createdValue.name,
        label: createdValue.name,
        description: createdValue.description ?? '',
        origin: 'user',
      });

      handleResolvedSelection(createdValue);
      return;
    }

    const option = optionsByValue.get(selectedValue);

    if (option) {
      handleResolvedSelection({
        name: option.value,
        description: option.description,
        source: option.source,
      });
    }
  };

  return (
    <Stack spacing={4}>
      <Select
        data={data}
        searchable
        size="md"
        disabled={loading || props.disabled}
        placeholder={loading ? loadingPlaceholder : 'Vyberte'}
        value={value?.name ?? null}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onChange={(selectedValue) => {
          handleSelectChange(selectedValue).catch(panic);
        }}
        itemComponent={PickerItem}
        nothingFound="Žiadne výsledky"
        {...props}
      />
      {value?.description && !hideDescription && (
        <P3Regular pl={24} pr={16} color="gray.6" sx={{ whiteSpace: 'pre-wrap' }}>
          {value.description}
        </P3Regular>
      )}
    </Stack>
  );
}
